/**
 * Where a CMS-authored URL actually points.
 *
 * Editors write links the way they think about the site - `/podroze-sluzbowe` - and the
 * site is served under a language prefix. Rendering that href as it stands produces a
 * link to `/podroze-sluzbowe`, which matches the `/:locale/*` route with a locale of
 * "podroze-sluzbowe", fails the known-locale check and redirects to the homepage. Every
 * call to action on the site did that: it looked like a working link and silently went
 * nowhere.
 *
 * So every URL out of the CMS goes through here. Anything already absolute, or a scheme
 * the browser handles itself, is left alone.
 */

/** A URL the app must not rewrite or route: another site, a mail client, an anchor. */
export function isExternalUrl(url: string): boolean {
  return /^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(url.trim());
}

/**
 * An address that can only be another site - a LinkedIn profile - as one the browser will
 * leave for. Pasted without its scheme, `linkedin.com/in/...` reads as a path on this site:
 * `resolveHref` would prefix the language and the link would land on a 404.
 */
export function offsiteHref(url: string): string {
  const value = url.trim();
  return isExternalUrl(value) ? value : `https://${value.replace(/^\/+/, '')}`;
}

/**
 * A CMS URL as a path inside this language.
 *
 * `/kontakt` and `kontakt` both become `/pl/kontakt`; an external URL comes back
 * untouched so the caller can tell the two apart by comparing.
 */
export function resolveHref(locale: string, url?: string | null): string {
  const value = (url ?? '').trim();
  if (!value) return '';
  if (isExternalUrl(value)) return value;

  const path = value.replace(/^\/+/, '');
  return path ? `/${locale}/${path}` : `/${locale}/`;
}
